import React, { memo, useMemo } from 'react';
import clsx from 'clsx';

import Image from '@/elements/Image/Image.component';
import Heading from '@/elements/Heading/Heading.component';
import Text from '@/elements/Text/Text.component';
import Button from '@/elements/Button/Button.component';
import ContactInfo from '@/components/ContactInfo/ContactInfo.component';

import { IProps } from '@/components/Card/Card.interface';
import '@/components/Card/Card.styles.css';

const Card: React.FC<IProps> = ({
  image,
  categories,
  date,
  title,
  text,
  contactInfoItems,
  cta,
  theme = 'white',
  className,
  cardType = 'grid',
}) => {
  // Memoize default CTA props to prevent unnecessary re-renders
  const defaultCtaProps = useMemo(
    () => ({
      variant: 'secondary' as const,
      color: 'primary' as const,
    }),
    [],
  );

  // Memoize categories rendering to avoid re-mapping on each render
  const categoriesElement = useMemo(() => {
    if (!categories?.length) return null;

    return (
      <div className="card__meta">
        {categories.map((item, index) => (
          <span key={`card-${item}-${index}`} className="card__category">
            {item}
          </span>
        ))}
      </div>
    );
  }, [categories]);

  // Memoize contact info rendering
  const contactInfoElement = useMemo(() => {
    if (!contactInfoItems?.length) return null;

    return (
      <div className="card__contactInfo">
        {contactInfoItems.map((item, index) => (
          <ContactInfo key={`card-contact-info-item-${index}`} {...item} />
        ))}
      </div>
    );
  }, [contactInfoItems]);

  // Memoize CTA rendering
  const ctaElement = useMemo(() => {
    if (!cta?.children) return null;

    return (
      <div className="card__cta">
        <Button {...defaultCtaProps} {...cta} />
      </div>
    );
  }, [cta, defaultCtaProps]);

  // Helper function to render common elements
  const renderCommonElements = () => {
    if (cardType === 'list') {
      return (
        <>
          <div className="flex-1">
            {title && (
              <Heading level={3} className="card__title" html={title} />
            )}
            {contactInfoElement}
            {text && <Text className="card__text" html={text} />}
            <div className="card-list__meta">
              {categoriesElement}
              {date && <div className="card__date">{date}</div>}
            </div>
          </div>
          {ctaElement}
        </>
      );
    }

    return (
      <>
        {title && <Heading level={3} className="card__title" html={title} />}
        {date && <div className="card__date">{date}</div>}
        {contactInfoElement}
        {text && <Text className="card__text" html={text} />}
        {categoriesElement}
        {ctaElement}
      </>
    );
  };

  return (
    <article
      className={clsx('card', className, {
        'card--list': cardType === 'list',
      })}
    >
      {image && (
        <div className="card__image">
          <Image {...image} rounded={true} />
        </div>
      )}
      <div className="card__content">{renderCommonElements()}</div>
    </article>
  );
};

export default memo(Card);
